// Ported from LC2 (src/commons/components/HomeTabs/Esg/EsgMetrics.js + EsgMetricsBox.js,
// merged into one file since the box was only ever used here). Same fields per row
// (title, value, optional display_tooltip/tooltip_text). Visual layer now matches the
// actual Figma frame (node 18158:8681 "Section → Form"): a fixed 3-column bordered grid of
// cells (icon, tooltip, label, value) instead of the earlier guess of a 4/6-column MUI
// Grid keyed off a `size` field the API never actually needed here — the Figma frame
// always lays these out 3-per-row regardless of row count. The FontAwesome question-mark
// tooltip icon is swapped for @mui/icons-material's HelpOutlineIcon (already an LC-new
// dependency).
//
// Icons are looked up by `row.title` (and the Font Awesome `row.icon` the /api/esg
// contract sends) so a reordered or partial metrics list still gets the right glyph.
// Position is only a fallback for an unrecognized title. Figma assets are used instead of
// `icon_element` (inline SVG HTML) so the cards stay on the design-system icons.
import React from 'react';
import PropTypes from 'prop-types';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import {tokens} from '../../../themes/ThemeCustomization';
import cashBackIcon from '../../../assets/esg/cash-back.svg';
import creditScoreIcon from '../../../assets/esg/credit-score.svg';
import savingsIcon from '../../../assets/esg/savings.svg';
import creditBuilderIcon from '../../../assets/esg/credit-builder.svg';
import onTimePaymentsIcon from '../../../assets/esg/on-time-payments.svg';
import visaDebitIcon from '../../../assets/esg/visa-debit.svg';
import expressPaycheckIcon from '../../../assets/esg/express-paycheck.svg';
import activeCheckingIcon from '../../../assets/esg/active-checking.svg';

const METRIC_ICONS = [
    cashBackIcon,
    creditScoreIcon,
    savingsIcon,
    creditBuilderIcon,
    onTimePaymentsIcon,
    visaDebitIcon,
    expressPaycheckIcon,
    activeCheckingIcon
];

// Exact titles from /api/esg third_block, plus the flat-mapper labels still used as a
// fallback until that older body is gone.
const METRIC_ICON_BY_TITLE = {
    'Cash Back earned with Stake': cashBackIcon,
    'Cash Back Earned': cashBackIcon,
    'Average credit score increase': creditScoreIcon,
    'Avg. Credit Score Increase': creditScoreIcon,
    'Average savings increase': savingsIcon,
    'Avg. Savings Increase': savingsIcon,
    'Residents on Credit Builder': creditBuilderIcon,
    'Credit Builder Enrollments': creditBuilderIcon,
    'On-time payments reported': onTimePaymentsIcon,
    'On-Time Payments Reported': onTimePaymentsIcon,
    'Activated Stake Visa Debit': visaDebitIcon,
    'Stake Visa Debit Activations': visaDebitIcon,
    'Express Paychecks delivered': expressPaycheckIcon,
    'Express Paychecks Delivered': expressPaycheckIcon,
    'Active Stake Checking accounts': activeCheckingIcon,
    'Active Checking Accounts': activeCheckingIcon
};

const METRIC_ICON_BY_FA = {
    faCircleDollar: cashBackIcon,
    faChartSimple: creditScoreIcon,
    faMoneyBillTrendUp: savingsIcon,
    faUsers: creditBuilderIcon,
    faCalendarClock: onTimePaymentsIcon,
    faBuildingColumns: visaDebitIcon,
    faBolt: expressPaycheckIcon,
    faMoneyCheck: activeCheckingIcon
};

const iconForMetric = (row, index) =>
    METRIC_ICON_BY_TITLE[row.title] || METRIC_ICON_BY_FA[row.icon] || METRIC_ICONS[index % METRIC_ICONS.length];

const EsgMetricsBox = ({data, icon}) => {
    const title = data.title ?? '';
    const value = data.value ?? '';
    const tooltipText = data.tooltip_text || [data.description_1, data.description_2].filter(Boolean).join(' ');
    const displayTooltip = data.display_tooltip ?? Boolean(tooltipText);

    return (
        <Box
            sx={{
                position: 'relative',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                rowGap: 2,
                backgroundColor: tokens.color.white,
                borderRight: `1px solid ${tokens.color.neutral200}`,
                borderBottom: `1px solid ${tokens.color.neutral200}`
            }}
        >
            <Box component="img" src={icon} alt="" sx={{width: 24, height: 24}} />
            <Stack rowGap={0.5}>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                    {value}
                </Typography>
            </Stack>
            {displayTooltip && tooltipText && (
                <Tooltip
                    arrow
                    title={
                        <Stack rowGap={0.5} sx={{p: 0.5}}>
                            {tooltipText.split('. ').map((str, idx) => (
                                <Typography key={idx} variant="caption">
                                    {str}
                                </Typography>
                            ))}
                        </Stack>
                    }
                >
                    <HelpOutlineIcon fontSize="small" sx={{position: 'absolute', top: 12, right: 11, color: tokens.color.neutral500}} />
                </Tooltip>
            )}
        </Box>
    );
};

EsgMetricsBox.propTypes = {
    data: PropTypes.object,
    icon: PropTypes.string
};

const EsgMetrics = ({data}) => {
    const title = data.title ?? '';
    const description = data.description ?? '';
    const rows = data.rows ?? [];

    return (
        <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', backgroundColor: tokens.color.white, p: 2}}>
            <Stack rowGap={0.5} sx={{pb: 2}}>
                <Typography variant="h5" fontWeight={600}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(166px, 1fr))',
                    border: `1px solid ${tokens.color.neutral200}`,
                    borderRadius: '6px',
                    overflow: 'hidden'
                }}
            >
                {rows.map((row, index) => (
                    <EsgMetricsBox key={`${row.title}-${index}`} data={row} icon={iconForMetric(row, index)} />
                ))}
            </Box>
        </Box>
    );
};

EsgMetrics.propTypes = {
    data: PropTypes.object
};

export default EsgMetrics;
